import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import Hotkeys from 'hotkeys-js';
import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
var defaultFilter = event => {
  var target = event.target || event.srcElement;
  var tagName = target.tagName;
  return !(target.isContentEditable || tagName === 'INPUT' || tagName === 'SELECT' || tagName === 'TEXTAREA');
};
var ReactHotkeys = /*#__PURE__*/React.forwardRef((props, ref) => {
  var {
    keyName,
    filter = defaultFilter,
    onKeyUp,
    onKeyDown,
    allowRepeat,
    disabled,
    splitKey,
    children
  } = props;
  var activeKeysRef = useRef(new Set());
  var currentHandleRef = useRef({});

  // 处理 keydown 事件
  var handleKeyDown = useCallback((keyboardEvent, hotkeysEvent) => {
    if (disabled) return;
    var shortcut = hotkeysEvent.shortcut;
    var isAlreadyPressed = activeKeysRef.current.has(shortcut);

    // 如果不允许重复且已经按下，则跳过
    if (isAlreadyPressed && !allowRepeat) return;

    // 记录当前按下的快捷键
    activeKeysRef.current.add(shortcut);
    currentHandleRef.current = hotkeysEvent;

    // 触发 onKeyDown 回调
    if (onKeyDown) {
      onKeyDown(shortcut, keyboardEvent, hotkeysEvent);
    }
  }, [disabled, allowRepeat, onKeyDown]);

  // 处理 keyup 事件 - 使用原生事件监听
  var handleKeyUp = useCallback(keyboardEvent => {
    if (disabled || !onKeyUp) return;

    // 检查是否有我们关心的按键被释放
    var currentPressed = Hotkeys.getPressedKeyString();
    var releasedKeys = Array.from(activeKeysRef.current).filter(key => {
      // 如果当前按键字符串中不包含这个快捷键，说明它被释放了
      return !currentPressed.includes(key);
    });

    // 处理被释放的按键
    releasedKeys.forEach(shortcut => {
      if (activeKeysRef.current.has(shortcut)) {
        activeKeysRef.current.delete(shortcut);

        // 构造 HotkeysEvent（基于最后记录的 handle）
        var hotkeysEvent = _extends({}, currentHandleRef.current, {
          shortcut
        });
        onKeyUp(shortcut, keyboardEvent, hotkeysEvent);
      }
    });

    // 清空 handle 如果没有按键被按下
    if (activeKeysRef.current.size === 0) {
      currentHandleRef.current = {};
    }
  }, [disabled, onKeyUp]);
  useImperativeHandle(ref, () => ({
    get isKeyDown() {
      return activeKeysRef.current.size > 0;
    },
    get handle() {
      return currentHandleRef.current;
    },
    get props() {
      return props;
    }
  }), [props]);
  useEffect(() => {
    // 设置过滤器
    if (filter) {
      Hotkeys.filter = filter;
    }

    // 绑定快捷键
    if (keyName) {
      Hotkeys.unbind(keyName);
      Hotkeys(keyName, {
        splitKey,
        keydown: true,
        keyup: true
      }, handleKeyDown);
    }
    return () => {
      if (keyName) {
        Hotkeys.unbind(keyName);
      }
      activeKeysRef.current.clear();
      currentHandleRef.current = {};
    };
  }, [keyName, splitKey, filter, handleKeyDown]);

  // 单独管理 keyup 事件监听
  useEffect(() => {
    if (!onKeyUp) return;
    var handleGlobalKeyUp = e => {
      // 使用 setTimeout 确保 hotkeys-js 内部状态已更新
      setTimeout(() => handleKeyUp(e), 0);
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('keyup', handleGlobalKeyUp);
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('keyup', handleGlobalKeyUp);
      }
    };
  }, [onKeyUp, handleKeyUp]);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: children
  });
});
ReactHotkeys.displayName = 'ReactHotkeys';
export default ReactHotkeys;